import {
  ButtonType,
  CheckBoxWithLabelWidget,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption
} from '@tableau/widgets';
import * as React from 'react';

import {
  Formatters,
  Messages
} from '../../codegen/Localize';
import { imgPath } from '../../common/utilities/ImgUrl';
import { AuthorizableType } from '../../permissions/types/AuthorizableType';
import {
  LockUnlockPermissionsState,
  NestedProjectsPermissionsState
} from '../../permissions/utils/LockStatusHelper';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { PermissionsLockDialogStyles } from '../styles/PermissionsLockDialogStyle';
import { PermissionsLockUnlockStyles } from '../styles/PermissionsLockUnlockStyle';
import { ContainerMessages } from './PermissionsLockUnlock';

const DIALOG_WIDTH = 500;

export const LOCK_DIALOG_TESTID = 'permissions-lock-dialog';
export const LOCK_DIALOG_LOCKED_TESTID = LOCK_DIALOG_TESTID + '-locked';
export const LOCK_DIALOG_UNLOCKED_TESTID = LOCK_DIALOG_TESTID + '-unlocked';
export const LOCK_DIALOG_NESTED_PROJECTS_TESTID = LOCK_DIALOG_TESTID + '-nested-projects';
export const LOCK_DIALOG_WARNING_MESSAGE_TESTID = LOCK_DIALOG_TESTID + '-warning-message';
export const LOCK_DIALOG_FOOTER_TESTID = LOCK_DIALOG_TESTID + '-footer';
export const LOCK_DIALOG_CANCEL_TESTID = LOCK_DIALOG_TESTID + '-cancel';
export const LOCK_DIALOG_SAVE_TESTID = LOCK_DIALOG_TESTID + '-save';

export enum PERMISSIONS_LOCK_RADIO_INDICIES {
  Mixed = -1, // negative because in the mixed state no radio button will be selected
  Locked = 0,
  Unlocked = 1,
}

const RadioIndexToLockState: { [key: number]: LockUnlockPermissionsState } = Object.freeze({
  [PERMISSIONS_LOCK_RADIO_INDICIES.Mixed]: LockUnlockPermissionsState.Mixed,
  [PERMISSIONS_LOCK_RADIO_INDICIES.Locked]: LockUnlockPermissionsState.Locked,
  [PERMISSIONS_LOCK_RADIO_INDICIES.Unlocked]: LockUnlockPermissionsState.Unlocked,
});
const lockState = (index: PERMISSIONS_LOCK_RADIO_INDICIES):
  LockUnlockPermissionsState => RadioIndexToLockState[index];

const LockStateToRadioIndex: { [key: number]: PERMISSIONS_LOCK_RADIO_INDICIES } = Object.freeze({
  [LockUnlockPermissionsState.Locked]: PERMISSIONS_LOCK_RADIO_INDICIES.Locked,
  [LockUnlockPermissionsState.Unlocked]: PERMISSIONS_LOCK_RADIO_INDICIES.Unlocked,
  [LockUnlockPermissionsState.Mixed]: PERMISSIONS_LOCK_RADIO_INDICIES.Mixed,
});
const lockRadioIndex = (state: LockUnlockPermissionsState):
  PERMISSIONS_LOCK_RADIO_INDICIES => LockStateToRadioIndex[state];

export type PermissionsLockDialogProps = {
  /** The strings to render */
  containerMessages: ContainerMessages;

  /** The number of container authorizables in this dialog. */
  containersNumber: number;

  /** Name of the (first) container authorizable in this dialog. */
  containerName: string;

  /** Type of container authorizable */
  containerAuthorizableType: AuthorizableType;

  /** Callback invoked when the dialog's cancel button is clicked. */
  onCancel: () => void;

  /** Callback invoked when the dialog's save button is clicked.
   * @param lockState the chosen lock state that will be saved
   */
  onSave: (lockState: LockUnlockPermissionsState, nestedProjectsState?: NestedProjectsPermissionsState) => void;

  /** The original lock state of the project(s) when the dialog is first opened */
  initialLockState: LockUnlockPermissionsState;

  /** The original nested project inclusion state of the project(s) when the dialog is first opened */
  initialNestedProjectsState: NestedProjectsPermissionsState;
};

export type PermissionsLockDialogState = {
  selectedLockUnlockIndex: number;
  selectedNestedProjectsState: NestedProjectsPermissionsState;
};

export class PermissionsLockDialog extends React.Component<PermissionsLockDialogProps, PermissionsLockDialogState> {
  public state: PermissionsLockDialogState = {
    selectedLockUnlockIndex: lockRadioIndex(this.props.initialLockState),
    selectedNestedProjectsState: this.props.initialNestedProjectsState,
  };

  private handleChangeLockOption = (lockState: LockUnlockPermissionsState, checkedIndex: number): void => {
    // when changing to locked, nested projects included is the default
    this.setState((prevState) => {
      return {
        selectedLockUnlockIndex: checkedIndex,
        selectedNestedProjectsState: lockState === LockUnlockPermissionsState.Locked ?
          NestedProjectsPermissionsState.Included :
          prevState.selectedNestedProjectsState,
      };
    });
  }

  private handleChangeNestedProjectsOption = (checked: boolean): void => {
    this.setState({ selectedNestedProjectsState: checked ? NestedProjectsPermissionsState.Included : NestedProjectsPermissionsState.Excluded });
  }

  private shouldEnableNestedProjectsOption = (): boolean => {
    return FeatureFlags.lockingSubProjectsEnabled && this.props.containerAuthorizableType === AuthorizableType.PROJECT;
  }

  private willLockContainers = (): boolean => {
    return (this.props.initialLockState !== LockUnlockPermissionsState.Locked)
      && (this.state.selectedLockUnlockIndex === PERMISSIONS_LOCK_RADIO_INDICIES.Locked);
  }

  private isSaveDisabled = (): boolean => {
    // disabled if the current selection wouldn't result in a change to the lock status

    const isLockStateSame = lockState(this.state.selectedLockUnlockIndex) === this.props.initialLockState;
    const isNestedProjectsSame = this.shouldEnableNestedProjectsOption() ?
      this.state.selectedNestedProjectsState === this.props.initialNestedProjectsState :
      true; // if no locking subprojects, only consider lock state, not nested projects state
    return isLockStateSame && isNestedProjectsSame;
  }

  private handleSave = (): void => {
    // nested projects state is ignored (basically default to excluded) if saving unlocked
    const lockOptionToSave: LockUnlockPermissionsState = lockState(this.state.selectedLockUnlockIndex);
    const nestedProjectsOptionToSave: NestedProjectsPermissionsState = lockOptionToSave === LockUnlockPermissionsState.Unlocked ? NestedProjectsPermissionsState.Excluded : this.state.selectedNestedProjectsState;
    this.props.onSave(lockOptionToSave, nestedProjectsOptionToSave);
  }

  private showWarningIncludingNested = (): boolean => {
    return this.shouldEnableNestedProjectsOption() && this.state.selectedNestedProjectsState === NestedProjectsPermissionsState.Included && this.props.containerMessages.warningIncludingNestedSingle !== undefined;
  }

  private renderWarningMessage = (): JSX.Element => {
    let warningMessage;
    if (this.props.containersNumber === 1) {
      warningMessage = this.showWarningIncludingNested() ?
        this.props.containerMessages.warningIncludingNestedSingle({CONTAINER_NAME: this.props.containerName}) :
        this.props.containerMessages.warningSingle({CONTAINER_NAME: this.props.containerName});
    } else {
       warningMessage = this.showWarningIncludingNested() ?
        this.props.containerMessages.warningIncludingNestedMultiple({
          CONTAINER_COUNT: this.props.containersNumber,
          CONTAINER_COUNT_LOC: Formatters.formatCount(this.props.containersNumber)
        }) :
        this.props.containerMessages.warningMultiple({
          CONTAINER_COUNT: this.props.containersNumber,
          CONTAINER_COUNT_LOC: Formatters.formatCount(this.props.containersNumber)
        });
    }
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <div className={PermissionsLockDialogStyles.WarningMessageContainer}>
        <span className={PermissionsLockDialogStyles.WarningIconContainer}>
          <img
            aria-hidden={true}
            src={imgPath('feedback_info.svg')}
          />
        </span>
        <span data-tb-test-id={LOCK_DIALOG_WARNING_MESSAGE_TESTID}>
          {warningMessage}
        </span>
      </div>
    );
  }

  private renderFooter = (): JSX.Element => {
    return (
      <DialogFooterButtonsWidget
        buttons={[
          {
            buttonType: ButtonType.Grey,
            children: Messages.cancel(),
            handleClick: this.props.onCancel,
            testId: LOCK_DIALOG_CANCEL_TESTID,
          }, {
            buttonType: ButtonType.Go,
            children: Messages.save(),
            disabled: this.isSaveDisabled(),
            handleClick: this.handleSave,
            testId: LOCK_DIALOG_SAVE_TESTID,
          }
        ]}
        testId={LOCK_DIALOG_FOOTER_TESTID}
      />
    );
  }

  private renderLockDialogRadioButtonLabel = (labelName: string, labelDescription: string, icon: string): JSX.Element => {
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <span>
        <img aria-hidden={true} className={PermissionsLockDialogStyles.StatusIcon} src={imgPath(icon)}/>
        <span className={PermissionsLockDialogStyles.RadioButtonLabel}>
          {labelName}
        </span>
        <span className={PermissionsLockDialogStyles.PermissionOptionDescription}>
          {labelDescription}
        </span>
      </span>
    );
  }

  private renderLockedRadioButtonLabel = (): JSX.Element => {
    return (
      <div className={PermissionsLockDialogStyles.RadioButtonLabelContainer}>
        {
          this.renderLockDialogRadioButtonLabel(
            Messages.lockContainerPermissions_lockHeader(),
            this.props.containerMessages.lockedRadioButtonLabel(),
            PermissionsLockUnlockStyles.LockedIcon
          )
        }
        {this.shouldEnableNestedProjectsOption() && this.renderNestedProjectsOption()}
        {this.willLockContainers() && this.renderWarningMessage()}
      </div>
    );
  }

  private renderUnlockedRadioButtonLabel = (): JSX.Element => {
    return this.renderLockDialogRadioButtonLabel(
      Messages.lockContainerPermissions_unlockHeader(),
      this.props.containerMessages.unlockedRadioButtonLabel(),
      PermissionsLockUnlockStyles.UnlockedIcon
    );
  }

  private renderLockUnlockRadioButtons = (): JSX.Element => {
    // label any type assertion is an approved workaround by the Widgets team because this older design
    // calls for a more complex label than just a string (what all newer designs should have)
    const lockingRadioButtons: RadioButtonWithLabelOption<LockUnlockPermissionsState>[] = [
      {
        label: this.renderLockedRadioButtonLabel() as any,
        testId: LOCK_DIALOG_LOCKED_TESTID,
        value: LockUnlockPermissionsState.Locked
      }, {
        label: this.renderUnlockedRadioButtonLabel() as any,
        testId: LOCK_DIALOG_UNLOCKED_TESTID,
        value: LockUnlockPermissionsState.Unlocked
      }
    ];

    return (
      <RadioButtonWithLabelGroupWidget
        buttonList={lockingRadioButtons}
        checkedIndex={this.state.selectedLockUnlockIndex}
        handleChange={this.handleChangeLockOption}
      />
    );
  }

  private renderNestedProjectsOption = (): JSX.Element => {
    return (
      <div className={PermissionsLockDialogStyles.NestedProjectsOption}>
        <CheckBoxWithLabelWidget
          checked={this.state.selectedNestedProjectsState === NestedProjectsPermissionsState.Included}
          disabled={this.state.selectedLockUnlockIndex !== PERMISSIONS_LOCK_RADIO_INDICIES.Locked}
          handleChange={this.handleChangeNestedProjectsOption}
          indeterminate={this.state.selectedNestedProjectsState === NestedProjectsPermissionsState.Mixed}
          label={Messages.lockProjectPermissions_nestedProjectsOption()}
          testId={LOCK_DIALOG_NESTED_PROJECTS_TESTID}
        />
      </div>
    );
  }

  public render(): JSX.Element {
    const title = this.props.containersNumber === 1 ?
      this.props.containerMessages.titleSingle() :
      this.props.containerMessages.titleMultiple({
        CONTAINER_COUNT: this.props.containersNumber,
        CONTAINER_COUNT_LOC: Formatters.formatCount(this.props.containersNumber)
      });
    return (
      <DialogWidget
        dialogWidth={DIALOG_WIDTH}
        isModal={true}
        onCloseRequest={this.props.onCancel}
        positioningBehavior={DialogPositioningBehavior.Centered}
        showCloseButton={false}
        showFooter={true}
        footerContent={this.renderFooter()}
        handleGlassClick={this.props.onCancel}
        testId={LOCK_DIALOG_TESTID}
        title={title}
        type={DialogType.NormalWithTitleBar}
        dialogStyle={{ zIndex: 20 }}
      >
        {this.renderLockUnlockRadioButtons()}
      </DialogWidget>
    );
  }
}
